import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { whenPopupFree, releasePopup } from "@/lib/launchPopups";
import {
  initializeFirebase,
  requestNotificationPermission,
  registerFCMToken,
} from "@/utils/firebase";

/**
 * First-login push notification prompt.
 *
 * Fires exactly once per user (keyed on user.id in localStorage) when:
 *   1. A user is signed in
 *   2. The browser supports the Notification API
 *   3. Permission is still `default` (never asked or dismissed the native modal)
 *
 * Wraps the native `Notification.requestPermission()` call in our own
 * branded modal so we get one clean "Enable" button click that both
 * requests permission AND registers the FCM token in a single user gesture
 * (required by Safari + some Android WebViews).
 */
export default function PushPermissionPrompt() {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    if (!user?.id || !token) return;
    // SSR / unsupported browsers.
    if (typeof window === "undefined" || !("Notification" in window)) return;
    // Already granted OR explicitly denied → the native permission is
    // sticky, so re-prompting is pointless.
    if (Notification.permission !== "default") return;

    const key = `yorm_push_prompt_seen_${user.id}`;
    if (localStorage.getItem(key)) return;

    // Small delay so the modal doesn't slam the user the instant they
    // land — lets the app finish its splash + first paint.
    // Wait for the location prompt so the two first-landing sheets never stack.
    let cancelQueue = () => {};
    const timer = setTimeout(() => { cancelQueue = whenPopupFree("push", () => setOpen(true)); }, 2000);
    return () => { clearTimeout(timer); cancelQueue(); releasePopup("push"); };
  }, [user?.id, token]);
  useEffect(() => {
    window.__yormPushPromptOpen = open;
    if (!open) releasePopup("push");
  }, [open]);

  const markSeen = () => {
    try {
      localStorage.setItem(`yorm_push_prompt_seen_${user.id}`, "1");
    } catch (e) { /* ignore quota errors */ }
  };

  const enable = async () => {
    setEnabling(true);
    try {
      await initializeFirebase();
      const fcmToken = await requestNotificationPermission();
      if (fcmToken) {
        const ok = await registerFCMToken(fcmToken, token);
        if (ok) {
          try { localStorage.setItem("yorm_fcm_token", fcmToken); } catch (e) { /* ignore */ }
          toast.success("Notifications enabled — you'll never miss a trip update.");
        } else {
          toast.error("Couldn't register with the server. Try again in Settings.");
        }
      } else if (Notification.permission === "denied") {
        toast.info("Notifications blocked. Enable them in browser settings.");
      }
    } catch (e) {
      toast.error("Couldn't enable · retry in Settings");
    } finally {
      markSeen();
      setEnabling(false);
      setOpen(false);
    }
  };

  const dismiss = () => {
    markSeen();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent hideCloseButton draggable
        className="max-w-sm sheet-quarter"
        data-testid="push-permission-modal"
      >
        <div className="flex flex-col items-center text-center pt-1">
          <div className="w-16 h-16 rounded-full bg-[#4F988E]/10 flex items-center justify-center">
            <Bell size={30} className="text-[#4F988E]" strokeWidth={2.25} />
          </div>
          <DialogHeader className="mt-4 space-y-2">
            <DialogTitle className="text-[22px] font-semibold tracking-[-0.02em] text-gray-900 text-center">
              Never miss a trip update
            </DialogTitle>
          </DialogHeader>
          <p className="text-[15px] leading-5 text-[rgba(60,60,67,0.7)] mt-2 max-w-[300px]">
            Get instant alerts when a driver accepts, counters, or arrives — even when
            the app is closed.
          </p>
        </div>
        <div className="space-y-2 pt-2">
          <Button
            onClick={enable}
            disabled={enabling}
            className="w-full h-12 rounded-full bg-[#4F988E] hover:bg-[#3d7a72] active:scale-[0.98] text-white text-[17px] font-semibold transition-[background-color,transform]"
            data-testid="push-permission-enable"
          >
            {enabling ? "Enabling…" : "Enable notifications"}
          </Button>
          <Button
            onClick={dismiss}
            variant="ghost"
            className="w-full h-12 rounded-full bg-[#4F988E]/10 hover:bg-[#4F988E]/20 active:scale-[0.98] text-[#4F988E] text-[17px] font-semibold transition-[background-color,transform]"
            data-testid="push-permission-skip"
          >
            Not now
          </Button>
          <p className="text-[12px] text-[rgba(60,60,67,0.6)] text-center pt-1">
            You can change this any time in your browser settings.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
